import React, { useEffect, useState } from "react";
import { getActiveStep, selectActiveLayer, setActiveLayer } from "../../../../../../../redux/toolsSlice";
import { getLayerObjConfig, removeLayerConfig, setLayerConfig, updateLayer } from '../../../../../../../redux/stepsSlice';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import axios from "axios";
import { getAuthToken } from "../../../../../utils/hub_client";
import { HUB_ENDPOINTS } from "../../../../../utils/endpoints";
import { getMaterial } from "../../../../../../../redux/materialSlice";
import { isHubRuntime } from "./hubResponseUtils";

export const FileUploadLayerDefaults = (id) => {
  return {
    id: id,
    type: 'file_upload',
    source: '',
    fileName: '',
    fileType: '',
  };
}

function FileUploadContent({ layer }) {

  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const layerObjConfig = useSelector((state) => getLayerObjConfig(state, layer), shallowEqual);
  const activeStep = useSelector((state) => getActiveStep(state));
  const stepId = activeStep.id;
  const activeLayer = useSelector((state) => selectActiveLayer(state));

  const defaultConfig = [
    {
      layerId: layer.id,
      sectionId: 'file_upload',
      sectionTitle: 'File Upload Element',
      values: []
    }
  ];

  const editElement = () => {
    removeFocusElement();
    dispatch(setActiveLayer(layer));
    dispatch(setLayerConfig(defaultConfig));
  }

  const removeFocusElement = () => {
    dispatch(setActiveLayer({}));
    dispatch(removeLayerConfig());
  }

  useEffect(() => {
    if (!layerObjConfig) return;
    const newLayer = {
      ...layer,
      ...layerObjConfig
    };
    dispatch(updateLayer({ stepId: stepId, layer: newLayer }));
  }, [layerObjConfig]);

  const handleDragOver = () => {
    setDragging(true);
  }

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
  }

  return (
    <>
      <FileUploadContentPreview
        onClick={editElement}
        layer={layer}
        dragging={dragging}
        onDragOver={handleDragOver}
        onDrop={handleDrop} />
    </>
  )
}

export const FileUploadContentPreview = ({ layer, onClick, dragging, onDragOver, onDrop }) => {
  const material = useSelector((state) => getMaterial(state));
  const audience = isHubRuntime();
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");

  // Author-configurable allowed types + max size (fall back to the prior defaults).
  const TYPE_MAP = {
    pdf: { mime: 'application/pdf', ext: '.pdf' },
    png: { mime: 'image/png', ext: '.png' },
    jpg: { mime: 'image/jpeg', ext: '.jpg' },
    jpeg: { mime: 'image/jpeg', ext: '.jpeg' },
    docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: '.docx' },
    csv: { mime: 'text/csv', ext: '.csv' },
  };
  const parseTypes = (s) => {
    const toks = String(s || '').toLowerCase().split(/[,\s]+/).filter(Boolean);
    const hits = toks.map((t) => TYPE_MAP[t]).filter(Boolean);
    return hits.length ? hits : Object.values(TYPE_MAP);
  };
  const parseMaxBytes = (s) => {
    const m = String(s || '').match(/(\d+(?:\.\d+)?)\s*(gb|mb|kb)?/i);
    if (!m) return 15 * 1024 * 1024;
    const unit = (m[2] || 'mb').toLowerCase();
    const mult = { kb: 1024, mb: 1024 * 1024, gb: 1024 * 1024 * 1024 }[unit];
    return Math.round(parseFloat(m[1]) * mult);
  };
  const allowed = parseTypes(layer?.allowedTypes);
  const allowedMimes = allowed.map((h) => h.mime);
  const acceptAttr = allowed.map((h) => h.ext).join(',');
  const maxBytes = parseMaxBytes(layer?.maxSize);
  const maxLabel = layer?.maxSize || '15 MB';
  const uploadLabel = layer?.label || 'Choose a file';

  const validateFileType = (fileType) => allowedMimes.includes(fileType);
  const validateFileSize = (fileSize) => fileSize <= maxBytes;

  const uploadFile = async (file, materialSlug) => {
    const formData = new FormData();
    formData.append('file', file);
    const authToken = getAuthToken();
    const headers = {
      'Content-Type': 'multipart/form-data',
      'Accept': 'application/json'
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await axios.post(HUB_ENDPOINTS.SAVE_FILE(materialSlug), formData, {
      headers,
    });
    return response.data;
  };

  const handleFileChange = (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    handleFile(file);
  }

  const handleFile = async (file) => {
      if (!file) {
        alert('Please select a file.');
        return;
      }

      if (!audience) {
        return;
      }

      const fallbackSlug = window.location?.pathname?.split("/")?.[3];
      const materialSlug = material?.slug || fallbackSlug;
      if (!materialSlug) {
        alert('Unable to determine path identifier for upload.');
        return;
      }

      if (!validateFileType(file?.type)) {
        alert(`Allowed file types: ${layer?.allowedTypes || 'PDF, PNG, JPG, JPEG, DOCX, CSV'}.`);
        return;
      }

      if (!validateFileSize(file.size)) {
        alert(`File size exceeds the maximum limit of ${maxLabel}.`);
        return;
      }

      try {
        const response = await uploadFile(file, materialSlug);
        if (response) {
          setUploadedFileName(response?.file_name || file.name);
          setUploadedFileUrl(response?.path || "");
          alert('File uploaded successfully.');
          return;
        }
        alert('Unable to upload file.');
      } catch (error) {
        const message = error?.response?.data?.error || 'Unable to upload file. Please try again.';
        alert(message);
        console.error('Error uploading file:', error);
      }
    }

  return (
    <>
      <div
        className='video-wrapper'
        onDragOver={onDragOver}
        onDrop={onDrop}>
        <div className="cover-upload-form" tabIndex={0} onClick={onClick}>
          <div className="filed-upload-form icon-filed-upload-form">
            <header className="text-upload-form">
              {uploadLabel} <br />Max file size: {maxLabel}
            </header>
            {audience ? (
              <label className="btn btn-primary save-btn" onClick={(event) => event.stopPropagation()}>
                <i className="fa fa-upload fa-lg" aria-hidden="true"></i> Browse File
                <input
                  type="file"
                  accept={acceptAttr}
                  onClick={(event) => event.stopPropagation()}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
            ) : (
              <div className="btn btn-primary save-btn disabled" aria-disabled="true">
                <i className="fa fa-upload fa-lg" aria-hidden="true"></i> Learner uploads on published path
              </div>
            )}
            {audience && uploadedFileName && (
              <div style={{ marginTop: 12, color: "#526683", fontSize: 14 }}>
                Uploaded file:{" "}
                {uploadedFileUrl ? (
                  <a href={uploadedFileUrl} target="_blank" rel="noreferrer">
                    {uploadedFileName}
                  </a>
                ) : (
                  uploadedFileName
                )}
              </div>
            )}
            {dragging && (
              <div className="dragging-overlay">
                Drop the image here
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default FileUploadContent;
